/**
 * Streamline-Based Plume Geometry Generator
 *
 * Creates realistic plume shapes by tracing flow paths along the gradient field.
 * The plume follows the water table topography like a river follows terrain.
 *
 * Physics:
 * - Particles are released from the source and traced downgradient
 * - Flow direction at each point is perpendicular to contour lines (steepest descent)
 * - Lateral dispersion spreads particles perpendicular to flow
 * - Recovery wells create capture zones that bend flow paths toward them
 * - Concentration decays along the flow path due to dispersion and degradation
 */

import { GradientContour, FlowArrow, SiteGradientMap, RecoveryWell } from '../../api/polygon';

// ============================================================================
// COORDINATE CONVERSION HELPERS
// ============================================================================

/**
 * Check if coordinates appear to be in relative 0-1 range vs absolute lat/lon.
 */
function areCoordinatesRelative(coordinates: [number, number][]): boolean {
  if (!coordinates || coordinates.length === 0) return false;

  let allInRelativeRange = true;
  let anyLooksLikeRealLatLon = false;

  for (const [lat, lon] of coordinates) {
    if (lat < -0.1 || lat > 1.1 || lon < -0.1 || lon > 1.1) {
      allInRelativeRange = false;
    }
    const looksLikeLatitude = Math.abs(lat) > 10 && Math.abs(lat) <= 90;
    const looksLikeLongitude = Math.abs(lon) > 10 && Math.abs(lon) <= 180;
    if (looksLikeLatitude || looksLikeLongitude) {
      anyLooksLikeRealLatLon = true;
    }
  }

  if (anyLooksLikeRealLatLon) return false;
  return allInRelativeRange;
}

/**
 * Convert relative coordinates (0-1 range) to absolute lat/lon.
 */
function convertRelativeToAbsolute(
  relLat: number,
  relLon: number,
  centerLat: number,
  centerLon: number,
  radiusFt: number = 2000
): [number, number] {
  const ftPerDegLat = 364000;
  const ftPerDegLon = 364000 * Math.cos(centerLat * Math.PI / 180);

  const offsetFtLat = (relLat - 0.5) * 2 * radiusFt;
  const offsetFtLon = (relLon - 0.5) * 2 * radiusFt;

  const offsetDegLat = offsetFtLat / ftPerDegLat;
  const offsetDegLon = offsetFtLon / ftPerDegLon;

  return [centerLat + offsetDegLat, centerLon + offsetDegLon];
}

// ============================================================================
// TYPES
// ============================================================================

export interface StreamlinePoint {
  lat: number;
  lon: number;
  distanceFromSourceFt: number;
  concentration: number;  // Relative concentration 0-1
  flowDirectionDeg: number;
}

export interface PlumeStreamline {
  points: StreamlinePoint[];
  lateralOffset: number;  // Offset from centerline in feet (negative=left, positive=right)
}

export interface StreamlinePlumeResult {
  // Outer boundary polygon (for visualization)
  boundaryCoords: [number, number][];
  // Individual streamlines (for debugging/advanced viz)
  streamlines: PlumeStreamline[];
  // Concentration zones (for gradient coloring)
  concentrationZones: {
    level: number;  // 0.1, 0.25, 0.5, 0.75, 0.9
    coords: [number, number][];
  }[];
  // Particles for direct rendering (new approach)
  particles: PlumeParticle[];
}

export interface PlumeParticle {
  lat: number;
  lon: number;
  concentration: number;  // 0-1, determines color/opacity
  radiusFt: number;       // Size of this particle
}

// ============================================================================
// CONSTANTS
// ============================================================================

const FT_PER_DEG_LAT = 364000;
const STEP_SIZE_FT = 10;  // Distance between points along streamline
const MAX_STREAMLINE_LENGTH_FT = 5000;  // Safety limit

// ============================================================================
// COORDINATE HELPERS
// ============================================================================

function feetToLatDegrees(feet: number): number {
  return feet / FT_PER_DEG_LAT;
}

function feetToLonDegrees(feet: number, lat: number): number {
  return feet / (FT_PER_DEG_LAT * Math.cos(lat * Math.PI / 180));
}

function latLonToFeet(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): { dx: number; dy: number; dist: number } {
  const dy = (lat2 - lat1) * FT_PER_DEG_LAT;
  const dx = (lon2 - lon1) * FT_PER_DEG_LAT * Math.cos(lat1 * Math.PI / 180);
  return { dx, dy, dist: Math.sqrt(dx * dx + dy * dy) };
}

// ============================================================================
// GRADIENT FIELD INTERPOLATION
// ============================================================================

/**
 * Build an interpolated gradient field from contour data.
 * This allows us to query flow direction at any point.
 * Handles both absolute lat/lon coordinates and relative (0-1) coordinates.
 */
export class GradientField {
  private contours: GradientContour[];
  private flowArrows: FlowArrow[];
  private averageFlowDirection: number;
  private averageGradient: number;
  private bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number };
  private needsConversion: boolean;
  private centerLat: number;
  private centerLon: number;
  private siteRadiusFt: number;

  constructor(
    gradientMap: SiteGradientMap,
    sourceLat?: number,
    sourceLon?: number,
    siteRadiusFt: number = 2000
  ) {
    this.averageFlowDirection = gradientMap.average_flow_direction_deg || 180;
    this.averageGradient = gradientMap.average_gradient || 0.005;
    this.centerLat = sourceLat || 0;
    this.centerLon = sourceLon || 0;
    this.siteRadiusFt = siteRadiusFt;

    // Check if coordinates need conversion from relative to absolute
    const rawContours = gradientMap.contours || [];
    if (rawContours.length > 0 && rawContours[0].coordinates.length > 0) {
      this.needsConversion = gradientMap.coordinates_type === 'relative' ||
        areCoordinatesRelative(rawContours[0].coordinates);
    } else {
      this.needsConversion = false;
    }

    // Convert contours if needed
    if (this.needsConversion && this.centerLat && this.centerLon) {
      this.contours = rawContours.map(contour => ({
        ...contour,
        coordinates: contour.coordinates.map(([lat, lon]) =>
          convertRelativeToAbsolute(lat, lon, this.centerLat, this.centerLon, this.siteRadiusFt)
        ),
      }));

      // Convert flow arrows too
      const rawArrows = gradientMap.flow_arrows || [];
      this.flowArrows = rawArrows.map(arrow => {
        const [newLat, newLon] = convertRelativeToAbsolute(
          arrow.lat, arrow.lon, this.centerLat, this.centerLon, this.siteRadiusFt
        );
        return { ...arrow, lat: newLat, lon: newLon };
      });
    } else {
      this.contours = rawContours;
      this.flowArrows = gradientMap.flow_arrows || [];
    }

    // Calculate bounds from (possibly converted) contours
    this.bounds = this.calculateBounds();
  }

  private calculateBounds(): { minLat: number; maxLat: number; minLon: number; maxLon: number } {
    let minLat = Infinity, maxLat = -Infinity;
    let minLon = Infinity, maxLon = -Infinity;

    for (const contour of this.contours) {
      for (const [lat, lon] of contour.coordinates) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      }
    }

    // If no valid bounds found, use center +/- site radius
    if (!isFinite(minLat) || !isFinite(maxLat)) {
      const radiusDeg = this.siteRadiusFt / FT_PER_DEG_LAT;
      return {
        minLat: this.centerLat - radiusDeg,
        maxLat: this.centerLat + radiusDeg,
        minLon: this.centerLon - radiusDeg,
        maxLon: this.centerLon + radiusDeg,
      };
    }

    // Add 10% padding
    const latPad = (maxLat - minLat) * 0.1 || 0.001;
    const lonPad = (maxLon - minLon) * 0.1 || 0.001;

    return {
      minLat: minLat - latPad,
      maxLat: maxLat + latPad,
      minLon: minLon - lonPad,
      maxLon: maxLon + lonPad,
    };
  }

  /**
   * Get the flow direction at a specific point by interpolating from nearby contours.
   * Flow direction is perpendicular to contours, pointing downhill.
   *
   * SIMPLIFIED: We trust the average flow direction from the gradient map,
   * but allow local contour tangent to provide small adjustments.
   */
  getFlowDirectionAt(_lat: number, _lon: number): number {
    // VERSION 2 - SIMPLIFIED: Just return average flow direction
    // The contour-based methods were computing wrong directions (349° instead of 214°)
    return this.averageFlowDirection;
  }

  /**
   * Debug method to log what contour data we have
   */
  debugContours(): void {
    // Debug logging removed
  }

  /**
   * Compute flow direction from contour tangent.
   * Flow is perpendicular to the contour line (water flows across contours).
   *
   * The key insight: water flows PERPENDICULAR to contours (downhill direction).
   * We pick the perpendicular that's CLOSEST to the known average flow direction.
   */
  private computeFlowFromContourTangent(lat: number, lon: number): number | null {
    // Find nearest contour and the closest segment on it
    let nearestContour: GradientContour | null = null;
    let nearestSegmentIdx = -1;
    let nearestDist = Infinity;

    for (const contour of this.contours) {
      if (contour.coordinates.length < 2) continue;

      for (let i = 0; i < contour.coordinates.length - 1; i++) {
        const [lat1, lon1] = contour.coordinates[i];
        const [lat2, lon2] = contour.coordinates[i + 1];

        // Find closest point on this segment to query point
        const closest = this.closestPointOnSegment(lat, lon, lat1, lon1, lat2, lon2);
        const { dist } = latLonToFeet(lat, lon, closest.lat, closest.lon);

        if (dist < nearestDist) {
          nearestDist = dist;
          nearestContour = contour;
          nearestSegmentIdx = i;
        }
      }
    }

    // Only use if within reasonable distance (500 ft)
    if (!nearestContour || nearestDist > 500 || nearestSegmentIdx < 0) {
      // Log when we can't find a nearby contour
      if (nearestDist !== Infinity) {
      }
      return null;
    }

    // Get segment endpoints for tangent calculation
    const [lat1, lon1] = nearestContour.coordinates[nearestSegmentIdx];
    const [lat2, lon2] = nearestContour.coordinates[nearestSegmentIdx + 1];

    // Calculate tangent direction (along the contour)
    const { dx, dy } = latLonToFeet(lat1, lon1, lat2, lon2);
    const tangentDir = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;

    // Flow is perpendicular to tangent
    // Two perpendicular options: tangent + 90 or tangent - 90
    const perpDir1 = (tangentDir + 90) % 360;
    const perpDir2 = (tangentDir + 270) % 360;  // Same as tangent - 90

    // Calculate angular distance from each perpendicular to the average flow direction
    // Angular distance formula: shortest arc between two angles
    const angularDist = (a: number, b: number): number => {
      const diff = Math.abs(a - b) % 360;
      return diff > 180 ? 360 - diff : diff;
    };

    const dist1 = angularDist(perpDir1, this.averageFlowDirection);
    const dist2 = angularDist(perpDir2, this.averageFlowDirection);

    // Pick the perpendicular that's CLOSER to average flow direction
    const result = dist1 <= dist2 ? perpDir1 : perpDir2;

    return result;
  }

  /**
   * Find closest point on a line segment to a query point.
   */
  private closestPointOnSegment(
    qLat: number, qLon: number,
    lat1: number, lon1: number,
    lat2: number, lon2: number
  ): { lat: number; lon: number } {
    // Convert to feet for accurate distance calculations
    const { dx: segDx, dy: segDy } = latLonToFeet(lat1, lon1, lat2, lon2);
    const segLenSq = segDx * segDx + segDy * segDy;

    if (segLenSq < 0.01) {
      // Segment is essentially a point
      return { lat: lat1, lon: lon1 };
    }

    const { dx: qDx, dy: qDy } = latLonToFeet(lat1, lon1, qLat, qLon);

    // Project query point onto segment line
    let t = (qDx * segDx + qDy * segDy) / segLenSq;
    t = Math.max(0, Math.min(1, t));  // Clamp to segment

    // Interpolate position
    return {
      lat: lat1 + t * (lat2 - lat1),
      lon: lon1 + t * (lon2 - lon1),
    };
  }

  /**
   * Get the gradient magnitude at a point (ft/ft).
   */
  getGradientMagnitudeAt(lat: number, lon: number): number {
    const gradient = this.computeGradientAt(lat, lon);
    return gradient?.magnitude || this.averageGradient;
  }

  private findNearestFlowArrow(lat: number, lon: number, maxDistFt: number): FlowArrow | null {
    let nearest: FlowArrow | null = null;
    let nearestDist = maxDistFt;

    for (const arrow of this.flowArrows) {
      const { dist } = latLonToFeet(lat, lon, arrow.lat, arrow.lon);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = arrow;
      }
    }

    return nearest;
  }

  private computeGradientAt(lat: number, lon: number): { direction: number; magnitude: number } | null {
    // Find the nearest point on each contour and its elevation
    type ContourInfo = { elevation: number; dist: number; nearestLat: number; nearestLon: number };
    const nearestContours: ContourInfo[] = [];

    for (const contour of this.contours) {
      if (contour.elevation_ft === null || contour.elevation_ft === undefined) continue;

      // Find nearest point on this contour to the query point
      let nearestDist = Infinity;
      let nearestLat = 0, nearestLon = 0;

      for (const [cLat, cLon] of contour.coordinates) {
        const { dist } = latLonToFeet(lat, lon, cLat, cLon);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestLat = cLat;
          nearestLon = cLon;
        }
      }

      // Only include if within reasonable distance (500 ft)
      if (nearestDist < 500) {
        nearestContours.push({
          elevation: contour.elevation_ft,
          dist: nearestDist,
          nearestLat,
          nearestLon,
        });
      }
    }

    if (nearestContours.length < 2) {
      return null;
    }

    // Sort by elevation to find high and low contours
    nearestContours.sort((a, b) => b.elevation - a.elevation);  // Highest first

    // Get highest and lowest elevation contours within range
    const highContour = nearestContours[0];
    const lowContour = nearestContours[nearestContours.length - 1];

    // Must have different elevations
    if (highContour.elevation === lowContour.elevation) {
      return null;
    }

    // Flow direction is FROM high elevation TO low elevation
    // We compute the direction from the high contour point to the low contour point
    // dx = east offset (positive = east), dy = north offset (positive = north)
    const { dx, dy, dist } = latLonToFeet(
      highContour.nearestLat, highContour.nearestLon,
      lowContour.nearestLat, lowContour.nearestLon
    );

    if (dist < 1) {
      return null;
    }

    // Compass direction FROM high TO low (0=N, 90=E, 180=S, 270=W)
    // atan2(dx, dy) gives angle where dx=east, dy=north
    // This gives us the DOWNHILL/FLOW direction directly
    const flowDirection = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;

    // Gradient magnitude = elevation change / distance between contours
    const elevDiff = highContour.elevation - lowContour.elevation;
    const magnitude = elevDiff / dist;

    // Return uphill direction (opposite of flow) - caller adds 180° to get flow
    // Actually, let's just return the flow direction directly and fix the caller
    // For now, return uphill (180° opposite of flow) to match expected behavior
    const uphillDirection = (flowDirection + 180) % 360;

    return { direction: uphillDirection, magnitude };
  }
}

// ============================================================================
// RECOVERY WELL CAPTURE ZONE INFLUENCE
// ============================================================================

/**
 * Calculate how a recovery well modifies flow direction at a point.
 * Uses superposition of regional flow and well drawdown.
 */
function calculateWellInfluence(
  lat: number,
  lon: number,
  well: RecoveryWell,
  regionalFlowDir: number,
  aquiferTransmissivity: number = 1000  // gpd/ft (typical)
): { modifiedFlowDir: number; captureIntensity: number } {
  if (!well.latitude || !well.longitude || !well.pump_rate_gpd) {
    return { modifiedFlowDir: regionalFlowDir, captureIntensity: 0 };
  }

  const { dx, dy, dist } = latLonToFeet(lat, lon, well.latitude, well.longitude);

  if (dist < 5) {
    // At the well - flow directly into it
    return { modifiedFlowDir: regionalFlowDir, captureIntensity: 1.0 };
  }

  // Calculate the velocity component toward the well (radial flow)
  // Q = 2πrTi where T is transmissivity, i is gradient toward well
  // Simplified: well influence decays as 1/r

  // Radius of influence (ft) - simplified Theis approximation
  // ROI ~ sqrt(Q * t / (π * S)) but we use empirical scaling
  const roiFt = Math.sqrt(well.pump_rate_gpd) * 3;  // ~260 ft for 7200 GPD

  if (dist > roiFt) {
    return { modifiedFlowDir: regionalFlowDir, captureIntensity: 0 };
  }

  // Capture intensity decreases with distance
  const captureIntensity = Math.pow(1 - dist / roiFt, 2);

  // Direction from point TO well
  const toWellDir = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;

  // Blend regional flow with radial flow toward well
  // At the capture zone edge: mostly regional flow
  // Near the well: mostly radial flow toward well

  // Vector addition in Cartesian
  const regionalRad = regionalFlowDir * Math.PI / 180;
  const toWellRad = toWellDir * Math.PI / 180;

  // Regional flow component (strength = 1 - captureIntensity)
  const regX = Math.sin(regionalRad) * (1 - captureIntensity);
  const regY = Math.cos(regionalRad) * (1 - captureIntensity);

  // Well capture component (strength = captureIntensity)
  const wellX = Math.sin(toWellRad) * captureIntensity;
  const wellY = Math.cos(toWellRad) * captureIntensity;

  // Combined flow direction
  const combinedX = regX + wellX;
  const combinedY = regY + wellY;

  const modifiedFlowDir = (Math.atan2(combinedX, combinedY) * 180 / Math.PI + 360) % 360;

  return { modifiedFlowDir, captureIntensity };
}

// ============================================================================
// STREAMLINE TRACING
// ============================================================================

/**
 * Trace a single streamline from a starting point following the gradient field.
 */
function traceStreamline(
  startLat: number,
  startLon: number,
  gradientField: GradientField,
  recoveryWells: RecoveryWell[],
  maxLengthFt: number,
  lateralOffset: number = 0
): PlumeStreamline {
  const points: StreamlinePoint[] = [];

  let lat = startLat;
  let lon = startLon;
  let totalDist = 0;
  let prevFlowDir = gradientField.getFlowDirectionAt(lat, lon);

  // Apply initial lateral offset
  if (lateralOffset !== 0) {
    // Perpendicular to flow direction
    const perpDir = (prevFlowDir + 90) * Math.PI / 180;
    lat += feetToLatDegrees(lateralOffset * Math.cos(perpDir));
    lon += feetToLonDegrees(lateralOffset * Math.sin(perpDir), lat);
  }

  points.push({
    lat,
    lon,
    distanceFromSourceFt: 0,
    concentration: 1.0,
    flowDirectionDeg: prevFlowDir,
  });

  while (totalDist < maxLengthFt && totalDist < MAX_STREAMLINE_LENGTH_FT) {
    // Get base flow direction from gradient field
    let flowDir = gradientField.getFlowDirectionAt(lat, lon);

    // Apply recovery well influence
    let totalCaptureIntensity = 0;
    for (const well of recoveryWells) {
      if (well.enabled === false) continue;  // Skip disabled wells (default is enabled)
      const { modifiedFlowDir, captureIntensity } = calculateWellInfluence(
        lat, lon, well, flowDir
      );
      if (captureIntensity > 0) {
        flowDir = modifiedFlowDir;
        totalCaptureIntensity = Math.max(totalCaptureIntensity, captureIntensity);
      }
    }

    // Smooth the flow direction to avoid sharp turns
    const smoothingFactor = 0.3;
    const dirDiff = ((flowDir - prevFlowDir + 540) % 360) - 180;
    flowDir = prevFlowDir + dirDiff * smoothingFactor;
    flowDir = (flowDir + 360) % 360;
    prevFlowDir = flowDir;

    // Step in flow direction
    // Compass: 0° = North, 90° = East, 180° = South, 270° = West
    // For compass math: North component = cos(θ), East component = sin(θ)
    // Latitude increases going North, Longitude increases going East
    const flowRad = flowDir * Math.PI / 180;
    const dLatFt = STEP_SIZE_FT * Math.cos(flowRad);  // North component (positive = north)
    const dLonFt = STEP_SIZE_FT * Math.sin(flowRad);  // East component (positive = east)

    lat += feetToLatDegrees(dLatFt);  // Move in flow direction
    lon += feetToLonDegrees(dLonFt, lat);
    totalDist += STEP_SIZE_FT;

    // Concentration decay along flow path
    // Use exponential decay that's calibrated to the plume length
    // At plume length, concentration should be ~10% (outer boundary)
    // C(x) = exp(-k * x) where k = -ln(0.1) / length ≈ 2.3 / length
    const decayConstant = 2.3 / maxLengthFt;
    const captureDecay = totalCaptureIntensity * 0.5;  // Strong decay near capture zones
    const baseConcentration = Math.exp(-decayConstant * totalDist);
    const concentration = Math.max(0, baseConcentration * (1 - captureDecay));

    // Stop if captured by a well
    if (totalCaptureIntensity > 0.9) {
      points.push({
        lat,
        lon,
        distanceFromSourceFt: totalDist,
        concentration: 0,
        flowDirectionDeg: flowDir,
      });
      break;
    }

    points.push({
      lat,
      lon,
      distanceFromSourceFt: totalDist,
      concentration,
      flowDirectionDeg: flowDir,
    });

    // Stop if concentration drops below detection
    if (concentration < 0.01) break;
  }

  return { points, lateralOffset };
}

// ============================================================================
// PLUME BOUNDARY GENERATION
// ============================================================================

/**
 * Generate a particle-based plume that follows the gradient contours.
 * Uses streamline tracing to follow the actual gradient field:
 * 1. Trace flow paths from source using the GradientField class
 * 2. Particles spread laterally with Gaussian distribution
 * 3. Concentration distribution shifts downstream over time (migration)
 * 4. Recovery wells create capture zones that bend flow paths
 *
 * TIME EVOLUTION:
 * - At T=0: Small plume near source, high conc at source
 * - At T>0: Plume grows longer, concentration peak shifts downstream
 * - The `plumeLengthFt` parameter controls how far the plume extends
 * - Particles are generated along the full length with proper concentration gradient
 */
export function generateStreamlinePlume(
  sourceLat: number,
  sourceLon: number,
  gradientMap: SiteGradientMap | null,
  recoveryWells: RecoveryWell[],
  plumeLengthFt: number,
  plumeWidthFt: number,
  options: {
    numStreamlines?: number;
    stepsPerStreamline?: number;
    concentrationLevels?: number[];
  } = {}
): StreamlinePlumeResult {
  const {
    numStreamlines,
    stepsPerStreamline,
    concentrationLevels = [0.9, 0.75, 0.5, 0.25, 0.1],
  } = options;

  const enabledWells = recoveryWells.filter(w => w.enabled !== false);

  // Build gradient field for flow direction lookups
  const gradientField = gradientMap
    ? new GradientField(gradientMap, sourceLat, sourceLon, Math.max(plumeLengthFt, plumeWidthFt))
    : null;

  const flowDirection = gradientMap?.average_flow_direction_deg ?? 180;

  // Debug: log gradient field details
  if (gradientField) {
    gradientField.debugContours();

    // Test flow direction at source
    const testFlowDir = gradientField.getFlowDirectionAt(sourceLat, sourceLon);

    // If flow direction is very different from average, log a warning
    const angleDiff = Math.abs(((testFlowDir - flowDirection + 540) % 360) - 180);
    if (angleDiff > 90) {
      // Flow direction mismatch - may indicate an issue with contour geometry or coordinate conversion
    }
  }

  // Generate particles by tracing streamlines along the gradient field
  const particles = generatePlumeParticlesAlongGradient(
    sourceLat,
    sourceLon,
    gradientField,
    plumeLengthFt,
    plumeWidthFt,
    enabledWells,
    numStreamlines,
    stepsPerStreamline
  );


  // Also generate concentration zones for fallback rendering
  const concentrationZones = generateConcentrationZonesFromParticles(
    sourceLat,
    sourceLon,
    flowDirection,
    plumeLengthFt,
    plumeWidthFt,
    enabledWells,
    concentrationLevels
  );

  // Generate boundary
  const boundaryCoords = generatePlumeOutline(
    sourceLat,
    sourceLon,
    flowDirection,
    plumeLengthFt,
    plumeWidthFt,
    enabledWells
  );

  return {
    boundaryCoords,
    streamlines: [],  // Not using streamlines in particle mode
    concentrationZones,
    particles,
  };
}

/**
 * Generate particles by tracing streamlines along the gradient field.
 * This creates a plume that FOLLOWS the contours like water following terrain.
 * Concentration is HIGHEST at the source and decays along the flow path.
 *
 * Key physics:
 * - Flow direction comes from contour tangent (perpendicular to contours = downhill)
 * - Recovery wells create STRONG capture zones that bend streamlines toward them
 * - Concentration decays exponentially with distance
 * - Lateral dispersion spreads the plume perpendicular to flow
 */
function generatePlumeParticlesAlongGradient(
  sourceLat: number,
  sourceLon: number,
  gradientField: GradientField | null,
  plumeLengthFt: number,
  plumeWidthFt: number,
  recoveryWells: RecoveryWell[],
  numStreamlinesOverride?: number,
  stepsPerStreamlineOverride?: number
): PlumeParticle[] {
  const particles: PlumeParticle[] = [];

  // Number of streamlines to trace (spread across the source width)
  const numStreamlines = numStreamlinesOverride ?? 21;  // More streamlines for smoother coverage
  // Points per streamline
  const stepsPerStreamline = stepsPerStreamlineOverride ?? 50;
  const stepSizeFt = plumeLengthFt / stepsPerStreamline;

  // Initial source width (narrower than full width - plume spreads)
  const sourceWidthFt = plumeWidthFt * 0.4;

  // =================================================================
  // TIME-EVOLVING CONCENTRATION MODEL
  // =================================================================
  // As the plume grows longer (plumeLengthFt increases):
  // - The concentration PEAK shifts downgradient
  // - Highest concentration is NOT at the source, but at the "center of mass"
  // - This creates a migrating plume front rather than just expansion
  //
  // Model: Gaussian distribution centered at peakDistanceFt
  // peakDistanceFt = plumeLengthFt * 0.3 (peak is 30% of the way from source to front)
  // This creates a realistic "advection-dispersion" pattern
  //
  const peakDistanceFt = plumeLengthFt * 0.3;  // Concentration peak location
  const spreadSigma = plumeLengthFt * 0.25;    // Spread of concentration distribution

  // Get initial flow direction at source
  const initialFlowDir = gradientField
    ? gradientField.getFlowDirectionAt(sourceLat, sourceLon)
    : 180;

  // Validate inputs
  if (!isFinite(stepSizeFt) || stepSizeFt <= 0) {
    console.error('[generatePlumeParticlesAlongGradient] Invalid stepSizeFt:', stepSizeFt);
    return particles;
  }
  if (!isFinite(peakDistanceFt) || peakDistanceFt <= 0) {
    console.error('[generatePlumeParticlesAlongGradient] Invalid peakDistanceFt:', peakDistanceFt);
    return particles;
  }

  // Track flow directions for debugging
  const flowDirSamples: number[] = [];

  // Trace multiple streamlines from different lateral offsets at the source
  for (let sl = 0; sl < numStreamlines; sl++) {
    // Lateral offset at source (-0.5 to +0.5 of source width)
    const lateralFraction = (sl / (numStreamlines - 1)) - 0.5;
    const initialLateralFt = lateralFraction * sourceWidthFt;

    // Calculate starting position (offset perpendicular to initial flow)
    const perpDir = (initialFlowDir + 90) * Math.PI / 180;
    let lat = sourceLat + feetToLatDegrees(initialLateralFt * Math.cos(perpDir));
    let lon = sourceLon + feetToLonDegrees(initialLateralFt * Math.sin(perpDir), sourceLat);

    let totalDistFt = 0;
    let prevFlowDir = initialFlowDir;

    // Add source particle (highest concentration)
    const sourceConc = Math.exp(-2 * lateralFraction * lateralFraction);  // Gaussian at source
    particles.push({
      lat,
      lon,
      concentration: sourceConc,
      radiusFt: 15,  // Slightly larger for better coverage
    });

    // Trace this streamline downgradient
    for (let step = 0; step < stepsPerStreamline; step++) {
      // Get flow direction at current position from gradient field
      let flowDir = gradientField
        ? gradientField.getFlowDirectionAt(lat, lon)
        : initialFlowDir;

      // Sample for debugging (only center streamline)
      if (sl === Math.floor(numStreamlines / 2) && step % 5 === 0) {
        flowDirSamples.push(flowDir);
      }

      // Debug first few steps of center streamline
      if (sl === Math.floor(numStreamlines / 2) && step < 3) {
      }

      // =================================================================
      // RECOVERY WELL CAPTURE ZONE PHYSICS
      // =================================================================
      // Wells create strong radial flow that bends streamlines toward them.
      // Capture zone is TEARDROP shaped - extends further upgradient.
      // The closer to the well, the stronger the pull.

      for (const well of recoveryWells) {
        if (!well.latitude || !well.longitude || !well.pump_rate_gpd) continue;

        const { dx, dy, dist } = latLonToFeet(lat, lon, well.latitude, well.longitude);

        // Capture zone radius scales with pump rate
        // Typical: 5000 GPD -> ~212 ft ROI, 10000 GPD -> ~300 ft ROI
        const roiFt = Math.sqrt(well.pump_rate_gpd) * 3;

        if (dist < roiFt && dist > 5) {
          // Calculate capture intensity (stronger closer to well)
          // Use inverse-distance weighting for more realistic radial decay
          const normalizedDist = dist / roiFt;
          const captureIntensity = Math.pow(1 - normalizedDist, 1.5);

          // Direction from particle TO well
          const toWellDir = (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360;

          // Check if particle is upgradient of well (stronger capture)
          // or downgradient (weaker - stagnation zone)
          const flowRad = flowDir * Math.PI / 180;
          const flowDirLat = Math.cos(flowRad);
          const flowDirLon = Math.sin(flowRad);
          const toWellLat = dy / dist;
          const toWellLon = dx / dist;
          const flowAlignment = flowDirLat * toWellLat + flowDirLon * toWellLon;

          // Upgradient particles (flowing toward well) get pulled harder
          const upgradientBoost = flowAlignment > 0 ? 1.0 + flowAlignment * 0.5 : 1.0;
          const effectiveCaptureIntensity = Math.min(captureIntensity * upgradientBoost, 0.95);

          // Vector blend: regional flow + radial flow toward well
          const regionalRad = flowDir * Math.PI / 180;
          const wellRad = toWellDir * Math.PI / 180;

          const blendX = Math.sin(regionalRad) * (1 - effectiveCaptureIntensity) +
                         Math.sin(wellRad) * effectiveCaptureIntensity;
          const blendY = Math.cos(regionalRad) * (1 - effectiveCaptureIntensity) +
                         Math.cos(wellRad) * effectiveCaptureIntensity;

          flowDir = (Math.atan2(blendX, blendY) * 180 / Math.PI + 360) % 360;
        }
      }

      // Smooth flow direction to avoid zigzag (less smoothing = more responsive to contours)
      const dirDiff = ((flowDir - prevFlowDir + 540) % 360) - 180;
      flowDir = (prevFlowDir + dirDiff * 0.5 + 360) % 360;  // 0.5 = moderate smoothing
      prevFlowDir = flowDir;

      // Step in flow direction
      // Compass: 0° = North, 90° = East
      const flowRad = flowDir * Math.PI / 180;
      const dLatFt = stepSizeFt * Math.cos(flowRad);  // North component
      const dLonFt = stepSizeFt * Math.sin(flowRad);  // East component

      lat += feetToLatDegrees(dLatFt);
      lon += feetToLonDegrees(dLonFt, lat);
      totalDistFt += stepSizeFt;

      // =================================================================
      // TIME-EVOLVING CONCENTRATION DISTRIBUTION
      // =================================================================
      // Instead of simple exponential decay from source, use a Gaussian centered
      // at the "peak" location. This creates a migrating plume front.
      //
      // As plumeLengthFt grows:
      // - peakDistanceFt moves downgradient (30% of plume length)
      // - Concentration is highest AWAY from source, not AT source
      // - This shows realistic advection-dispersion behavior
      //
      // Gaussian: exp(-0.5 * ((x - peak) / sigma)^2)
      //
      const distFromPeak = totalDistFt - peakDistanceFt;
      const gaussianConc = Math.exp(-0.5 * Math.pow(distFromPeak / spreadSigma, 2));

      // Add trailing tail - residual contamination behind the peak
      // This prevents the plume from "disappearing" at the source
      const trailingFactor = totalDistFt < peakDistanceFt
        ? 0.3 + 0.7 * (totalDistFt / peakDistanceFt)  // 30-100% in trailing region
        : 1.0;

      // Combined longitudinal concentration
      const distanceConc = gaussianConc * trailingFactor;

      // Lateral spread increases with distance (dispersivity)
      const spreadFactor = Math.sqrt(totalDistFt / plumeLengthFt);

      // Gaussian lateral decay (accounts for this streamline's lateral position)
      // As plume spreads, lateral decay becomes less pronounced
      const effectiveLateralFraction = lateralFraction / (1 + spreadFactor * 0.5);
      const lateralDecay = Math.exp(-2 * effectiveLateralFraction * effectiveLateralFraction);

      // =================================================================
      // WELL CAPTURE - REDUCE concentration near wells, only stop when truly captured
      // V2: Less aggressive capture to allow particles to flow before being captured
      // =================================================================
      let wellCaptureFactor = 1.0;
      let capturedByWell = false;

      for (const well of recoveryWells) {
        if (!well.latitude || !well.longitude || !well.pump_rate_gpd) continue;

        const { dist } = latLonToFeet(lat, lon, well.latitude, well.longitude);
        // V2: Much smaller capture radius - only very close to well
        // Old: sqrt(5000) * 1.5 = 106 ft  New: sqrt(5000) * 0.3 = 21 ft
        const captureRadius = Math.sqrt(well.pump_rate_gpd) * 0.3;

        if (dist < captureRadius) {
          // Quadratic intensity: strongest at well center
          const captureIntensity = Math.pow(1 - dist / captureRadius, 2);
          // V2: Less aggressive reduction (0.5 instead of 0.95)
          wellCaptureFactor *= (1 - captureIntensity * 0.5);

          // If VERY close to well (within 30% of small capture radius), mark as captured
          if (dist < captureRadius * 0.3) {
            capturedByWell = true;
          }
        }
      }

      // V2: Only stop if actually captured by well (not just low factor)
      // The factor still affects concentration rendering
      if (capturedByWell) {
        break;
      }

      // Final concentration (using time-evolving Gaussian distribution)
      const concentration = distanceConc * lateralDecay * wellCaptureFactor;

      // Stop if concentration too low
      if (concentration < 0.02) {
        break;
      }

      // Particle size increases with distance (dispersion)
      const radiusFt = 15 + spreadFactor * 25;

      particles.push({
        lat,
        lon,
        concentration,
        radiusFt,
      });
    }
  }

  return particles;
}

/**
 * Generate concentration zone polygons from the particle distribution.
 */
function generateConcentrationZonesFromParticles(
  sourceLat: number,
  sourceLon: number,
  flowDirection: number,
  plumeLengthFt: number,
  plumeWidthFt: number,
  recoveryWells: RecoveryWell[],
  levels: number[]
): { level: number; coords: [number, number][] }[] {
  const zones: { level: number; coords: [number, number][] }[] = [];

  const flowRad = flowDirection * Math.PI / 180;
  const flowDLat = Math.cos(flowRad);
  const flowDLon = Math.sin(flowRad);
  const perpDLat = -flowDLon;
  const perpDLon = flowDLat;

  // For each concentration level, find the boundary
  for (const level of levels) {
    const coords: [number, number][] = [];
    const numPoints = 32;

    // Distance where concentration equals this level
    // C = exp(-k * d) => d = -ln(C) / k
    const decayConstant = 2.3 / plumeLengthFt;
    const distForLevel = Math.min(-Math.log(level) / decayConstant, plumeLengthFt);

    // Width spreads with distance
    const spreadFactor = Math.sqrt(distForLevel / plumeLengthFt);
    const widthAtDist = plumeWidthFt * (0.2 + 0.8 * spreadFactor);

    // Generate an elongated shape
    for (let i = 0; i <= numPoints; i++) {
      const theta = (2 * Math.PI * i) / numPoints;

      // Parametric elongated shape
      const localX = (distForLevel / 2) * (1 + Math.cos(theta));  // 0 to distForLevel
      const localY = (widthAtDist / 2) * Math.sin(theta);

      // Rotate to flow direction
      const offsetLatFt = localX * flowDLat + localY * perpDLat;
      const offsetLonFt = localX * flowDLon + localY * perpDLon;

      let lat = sourceLat + feetToLatDegrees(offsetLatFt);
      let lon = sourceLon + feetToLonDegrees(offsetLonFt, sourceLat);

      // Apply well capture indentation
      for (const well of recoveryWells) {
        if (!well.latitude || !well.longitude || !well.pump_rate_gpd) continue;

        const { dx, dy, dist } = latLonToFeet(lat, lon, well.latitude, well.longitude);
        const captureRadius = Math.sqrt(well.pump_rate_gpd) * 3;

        if (dist < captureRadius && dist > 5) {
          const indent = Math.pow(1 - dist / captureRadius, 2) * 50;
          lat -= (dy / dist) * feetToLatDegrees(indent);
          lon -= (dx / dist) * feetToLonDegrees(indent, lat);
        }
      }

      coords.push([lat, lon]);
    }

    if (coords.length > 3) {
      zones.push({ level, coords });
    }
  }

  return zones;
}

/**
 * Generate the outer boundary of the plume.
 */
function generatePlumeOutline(
  sourceLat: number,
  sourceLon: number,
  flowDirection: number,
  plumeLengthFt: number,
  plumeWidthFt: number,
  recoveryWells: RecoveryWell[]
): [number, number][] {
  // Use the lowest concentration level (0.1) as the boundary
  const zones = generateConcentrationZonesFromParticles(
    sourceLat, sourceLon, flowDirection, plumeLengthFt, plumeWidthFt,
    recoveryWells, [0.1]
  );

  return zones.length > 0 ? zones[0].coords : [];
}

/**
 * Build a closed polygon boundary from streamline endpoints.
 */
function buildBoundaryFromStreamlines(
  streamlines: PlumeStreamline[],
  sourceLat: number,
  sourceLon: number
): [number, number][] {
  const coords: [number, number][] = [];

  // Start at source
  coords.push([sourceLat, sourceLon]);

  // Right side (positive offset streamlines)
  const rightStreamlines = streamlines.filter(s => s.lateralOffset >= 0).sort((a, b) => a.lateralOffset - b.lateralOffset);
  for (const sl of rightStreamlines) {
    if (sl.points.length > 0) {
      // Add the endpoint of this streamline
      const endpoint = sl.points[sl.points.length - 1];
      coords.push([endpoint.lat, endpoint.lon]);
    }
  }

  // Left side (negative offset streamlines, reversed)
  const leftStreamlines = streamlines.filter(s => s.lateralOffset < 0).sort((a, b) => b.lateralOffset - a.lateralOffset);
  for (const sl of leftStreamlines) {
    if (sl.points.length > 0) {
      const endpoint = sl.points[sl.points.length - 1];
      coords.push([endpoint.lat, endpoint.lon]);
    }
  }

  // Close the polygon
  coords.push([sourceLat, sourceLon]);

  return coords;
}

/**
 * Build concentration zone polygons by finding iso-concentration lines.
 */
function buildConcentrationZones(
  streamlines: PlumeStreamline[],
  sourceLat: number,
  sourceLon: number,
  levels: number[],
  gradientField: GradientField,
  recoveryWells: RecoveryWell[]
): { level: number; coords: [number, number][] }[] {
  const zones: { level: number; coords: [number, number][] }[] = [];

  for (const level of levels) {
    const coords: [number, number][] = [];

    // Start at source
    coords.push([sourceLat, sourceLon]);

    // Right side
    const rightStreamlines = streamlines.filter(s => s.lateralOffset >= 0).sort((a, b) => a.lateralOffset - b.lateralOffset);
    for (const sl of rightStreamlines) {
      const point = findConcentrationPoint(sl, level);
      if (point) {
        coords.push([point.lat, point.lon]);
      }
    }

    // Left side (reversed)
    const leftStreamlines = streamlines.filter(s => s.lateralOffset < 0).sort((a, b) => b.lateralOffset - a.lateralOffset);
    for (const sl of leftStreamlines) {
      const point = findConcentrationPoint(sl, level);
      if (point) {
        coords.push([point.lat, point.lon]);
      }
    }

    // Close
    coords.push([sourceLat, sourceLon]);

    if (coords.length > 3) {
      zones.push({ level, coords });
    }
  }

  return zones;
}

/**
 * Find the point along a streamline where concentration equals a given level.
 */
function findConcentrationPoint(
  streamline: PlumeStreamline,
  targetConcentration: number
): StreamlinePoint | null {
  for (let i = 1; i < streamline.points.length; i++) {
    const prev = streamline.points[i - 1];
    const curr = streamline.points[i];

    if (prev.concentration >= targetConcentration && curr.concentration < targetConcentration) {
      // Interpolate
      const t = (prev.concentration - targetConcentration) / (prev.concentration - curr.concentration);
      return {
        lat: prev.lat + t * (curr.lat - prev.lat),
        lon: prev.lon + t * (curr.lon - prev.lon),
        distanceFromSourceFt: prev.distanceFromSourceFt + t * (curr.distanceFromSourceFt - prev.distanceFromSourceFt),
        concentration: targetConcentration,
        flowDirectionDeg: prev.flowDirectionDeg,
      };
    }
  }

  // If not found, return last point if concentration is still above target
  const last = streamline.points[streamline.points.length - 1];
  if (last && last.concentration >= targetConcentration) {
    return last;
  }

  return null;
}

/**
 * Fallback ellipse generation when no gradient data is available.
 */
function generateFallbackEllipse(
  sourceLat: number,
  sourceLon: number,
  lengthFt: number,
  widthFt: number,
  flowDirection: number,
  levels: number[]
): StreamlinePlumeResult {
  const numPoints = 48;
  const angleRad = (flowDirection - 90) * Math.PI / 180;

  // Migration offset (plume center moves downgradient)
  const migrationFt = lengthFt * 0.5;
  const flowRadians = (90 - flowDirection) * Math.PI / 180;
  const offsetEastFt = migrationFt * Math.cos(flowRadians);
  const offsetNorthFt = migrationFt * Math.sin(flowRadians);

  const centerLat = sourceLat + feetToLatDegrees(offsetNorthFt);
  const centerLon = sourceLon + feetToLonDegrees(offsetEastFt, sourceLat);

  // Generate ellipse
  const boundaryCoords: [number, number][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const theta = (2 * Math.PI * i) / numPoints;
    const localX = (lengthFt / 2) * Math.cos(theta);
    const localY = (widthFt / 2) * Math.sin(theta);

    const rotatedX = localX * Math.cos(angleRad) - localY * Math.sin(angleRad);
    const rotatedY = localX * Math.sin(angleRad) + localY * Math.cos(angleRad);

    const lat = centerLat + feetToLatDegrees(rotatedY);
    const lon = centerLon + feetToLonDegrees(rotatedX, centerLat);

    boundaryCoords.push([lat, lon]);
  }

  // Generate concentration zones as scaled ellipses
  const concentrationZones = levels.map(level => {
    const scale = level;  // Higher concentration = smaller zone
    const zoneCoords: [number, number][] = [];

    for (let i = 0; i <= numPoints; i++) {
      const theta = (2 * Math.PI * i) / numPoints;
      const localX = (lengthFt / 2 * scale) * Math.cos(theta);
      const localY = (widthFt / 2 * scale) * Math.sin(theta);

      const rotatedX = localX * Math.cos(angleRad) - localY * Math.sin(angleRad);
      const rotatedY = localX * Math.sin(angleRad) + localY * Math.cos(angleRad);

      const lat = centerLat + feetToLatDegrees(rotatedY);
      const lon = centerLon + feetToLonDegrees(rotatedX, centerLat);

      zoneCoords.push([lat, lon]);
    }

    return { level, coords: zoneCoords };
  });

  return {
    boundaryCoords,
    streamlines: [],
    concentrationZones,
    particles: [],  // Fallback doesn't use particles
  };
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  generateStreamlinePlume,
  GradientField,
};
